import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { Observable, filter, map, of, forkJoin, switchMap, tap, from, catchError, finalize, shareReplay, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { enModoPreview } from '../dev/preview-mode';
import {
  ProjectsApiResponse,
  ProjectByCodeResponse,
  ProjectStatusResponse,
  ProjectTypesResponse,
  RegionsResponse,
  CompaniesResponse,
  BusinessUnitsResponse,
  CountriesResponse,
  ProductTypesResponse,
  ResponsiblesRolesResponse,
  ProjectDetailResponse
} from '../core/interfaces/project-api.interface';

const ID_TOKEN_SCOPES = ['openid', 'profile'];

/** Una persona del directorio del tenant, tal como la devuelve Graph */
export interface PersonaDelTenant {
  id: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
  jobTitle: string | null;
  department: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private msalInitialized = false;

  private idTokenFromLogin: string | null = null;

  constructor() {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((event: EventMessage) => {
        const result = event.payload as AuthenticationResult;
        console.log('Login successful', result);
        if (result?.idToken) {
          this.idTokenFromLogin = result.idToken;
          this.venceEn = this.vencimientoDe(result);
        }

        /* Entró: el tope de un solo reintento automático se libera. Si no, la
           próxima sesión que venza en esta misma pestaña ya no se renovaría
           sola y habría que apretar el botón por algo que ya funciona. */
        try {
          sessionStorage.removeItem('loginReintentoSSO');
        } catch { /* una pestaña sin storage no es motivo para no entrar */ }

        this.fetchProjectsAfterLogin(event);
      });
    this.initializeMsal();
  }

  async initializeMsal(): Promise<void> {
    if (!this.msalInitialized && typeof (this.msalService as unknown as { initialize?: () => Promise<void> }).initialize === 'function') {
      await (this.msalService as unknown as { initialize: () => Promise<void> }).initialize();
      this.msalInitialized = true;
    } else {
      this.msalInitialized = true;
    }
  }

  /**
   * El token de sesión, pidiéndoselo a MSAL **cada vez**.
   *
   * Antes empezaba con `if (this.idTokenFromLogin) return of(...)`: el primer
   * token que llegaba se guardaba y **se devolvía para siempre**. Un id token
   * de Azure dura alrededor de una hora, así que pasado ese rato la sesión
   * seguía usando uno vencido, la API respondía 401 en todo, y no había forma
   * de recuperarse sin recargar la página: el atajo salteaba justamente la
   * llamada que renueva.
   *
   * `acquireTokenSilent` ya tiene su propio caché —no sale a la red si el
   * token sigue vigente— así que preguntar siempre no cuesta nada y renueva
   * solo cuando hace falta.
   *
   * El token guardado queda como respaldo para el momento en que todavía no
   * hay cuenta: justo después del redirect de login.
   */
  /** Cuándo vence el token guardado, en milisegundos de reloj */
  private venceEn = 0;

  /** La renovación en curso, si hay una */
  private renovacion$: Observable<string | null> | null = null;

  /**
   * Margen con el que se pide uno nuevo antes de que venza.
   *
   * Un token que vence en veinte segundos alcanza para salir de acá y no para
   * llegar: la petición viaja, el servidor la mira y para entonces ya no vale.
   */
  private static readonly MARGEN = 5 * 60 * 1000;

  private vigente(): string | null {
    return this.idTokenFromLogin && Date.now() < this.venceEn - AuthService.MARGEN
      ? this.idTokenFromLogin
      : null;
  }

  private getTokenFromMsal(): Observable<string | null> {
    /* Mientras el guardado sirva, se usa. No es el atajo de antes —aquel no
       miraba el vencimiento y devolvía el mismo para siempre—: acá el que
       venció no pasa este filtro y se sale a renovar. */
    const guardado = this.vigente();
    if (guardado) return of(guardado);

    const account = this.getAccount();
    if (!account) return of(this.idTokenFromLogin);

    /* Una sola renovación a la vez. La pantalla dispara ocho o diez llamadas
       juntas y todas se encuentran con el token vencido: sin esto, cada una
       abre su propia renovación —y su propio iframe— para traer el mismo
       token. Se comparte la primera. */
    if (this.renovacion$) return this.renovacion$;

    this.renovacion$ = from(this.pedirTokenAMsal(account)).pipe(
      map((result: AuthenticationResult | null) => {
        const token = result?.idToken ?? null;
        if (token && result) {
          this.idTokenFromLogin = token;
          this.venceEn = this.vencimientoDe(result);
        }
        return token;
      }),
      /* Si no se puede renovar en silencio, la sesión se terminó de verdad.
         Se devuelve el guardado por si sirve; el interceptor de 401 se encarga
         de mandar a iniciar sesión cuando tampoco alcance. */
      catchError(() => of(this.idTokenFromLogin)),
      finalize(() => { this.renovacion$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.renovacion$;
  }

  /**
   * Le pide el token a MSAL, y si vuelve vencido lo vuelve a pedir en serio.
   *
   * `acquireTokenSilent` renueva el token **de acceso** de los scopes que se
   * le piden; el de identidad lo sirve del caché y puede estar vencido hace
   * rato. Eso es lo que pasaba: el back rechazaba con «Expired token» y desde
   * acá parecía que la sesión estaba viva, porque MSAL contestaba sin error.
   *
   * Con `forceRefresh` sale a la red aunque tenga algo guardado. No se usa
   * siempre —sería una llamada a Azure por cada renovación— sino solo cuando
   * lo que devolvió no sirve.
   */
  private async pedirTokenAMsal(account: AccountInfo): Promise<AuthenticationResult | null> {
    const pedir = (forceRefresh: boolean) =>
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: ID_TOKEN_SCOPES,
        forceRefresh
      });

    const primero = await pedir(false);

    if (this.sirve(primero)) return primero;

    return pedir(true);
  }

  /** Si el token que devolvió MSAL todavía alcanza para hacer una llamada */
  private sirve(result: AuthenticationResult | null): boolean {
    if (!result?.idToken) return false;

    return Date.now() < this.vencimientoDe(result) - AuthService.MARGEN;
  }

  /**
   * Cuándo vence, según el propio token.
   *
   * `expiresOn` de MSAL habla del token de acceso; el que mandamos nosotros es
   * el de identidad, y su vencimiento está en el claim `exp` (en segundos).
   * Si no viene, se toma una hora, que es lo que dura un id token de Azure.
   */
  private vencimientoDe(result: AuthenticationResult): number {
    const exp = (result.idTokenClaims as { exp?: number } | undefined)?.exp;
    return exp ? exp * 1000 : Date.now() + 60 * 60 * 1000;
  }

  /**
   * La sesión terminó de verdad: se borra la cuenta del caché.
   *
   * Olvidar el token no alcanza. La cuenta queda en el almacenamiento de
   * MSAL, `isLoggedIn()` sigue diciendo que sí, y la pantalla de login
   * rebotaba al tablero: 401, vuelta al login, y otra vez. Sin cuenta, el
   * login se queda quieto y ofrece iniciar sesión, que es lo único que
   * arregla la situación.
   *
   * No navega: de eso se encarga quien la llama.
   */
  cerrarSesionLocal(): void {
    this.olvidarToken();

    /* Si falla, no se rompe nada: el login ya no rebota por su cuenta. */
    Promise.resolve(this.msalService.instance.clearCache?.()).catch(() => undefined);
  }

  /**
   * Quién es el que está adentro, según el directorio.
   *
   * El token de sesión trae el nombre y el correo, y nada más: el puesto y el
   * área sólo aparecen en el token si el tenant los agregó como claim, cosa
   * que este no hizo. Se piden a `/me`, que alcanza con el permiso básico de
   * la propia sesión.
   */
  miPerfilDelTenant(): Observable<PersonaDelTenant | null> {
    const account = this.getAccount();

    if (!account) return of(null);

    return from(
      this.msalService.instance.acquireTokenSilent({ account, scopes: ['User.Read'] })
    ).pipe(
      switchMap((r) => this.http.get<PersonaDelTenant>(
        'https://graph.microsoft.com/v1.0/me'
          + '?$select=id,displayName,mail,userPrincipalName,jobTitle,department',
        { headers: { Authorization: `Bearer ${r.accessToken}` } }
      )),
      catchError(() => of(null))
    );
  }

  /**
   * El puesto y el área de un grupo de personas, buscadas por correo.
   *
   * **Es lo mismo que Estima muestra**, y sale del mismo lado: el directorio
   * del tenant. Nuestra base sólo guarda el nombre y el correo, así que las
   * columnas Puesto y Área salían vacías para todo el que no se hubiera dado
   * de alta desde el buscador del directorio.
   *
   * Va por tandas: el `$filter` de Graph tiene un largo máximo y con veinte
   * correos ya se pasa. Si una tanda falla, las otras igual llegan —media
   * columna llena es mejor que ninguna— y por eso cada una atrapa su error.
   */
  datosDelTenantPorCorreo(correos: string[]): Observable<PersonaDelTenant[]> {
    const account = this.getAccount();
    const limpios = [...new Set(correos.filter((c) => !!c).map((c) => c.toLowerCase()))];

    if (!account || limpios.length === 0) return of([]);

    const tandas: string[][] = [];

    for (let i = 0; i < limpios.length; i += 10) {
      tandas.push(limpios.slice(i, i + 10));
    }

    return from(
      this.msalService.instance.acquireTokenSilent({ account, scopes: ['User.ReadBasic.All'] })
    ).pipe(
      switchMap((r) => {
        const headers = { Authorization: `Bearer ${r.accessToken}` };

        const pedidos = tandas.map((tanda) => {
          const filtro = tanda
            .map((c) => {
              const escapado = c.replace(/'/g, "''");
              return `mail eq '${escapado}' or userPrincipalName eq '${escapado}'`;
            })
            .join(' or ');

          return this.http.get<{ value: PersonaDelTenant[] }>(
            'https://graph.microsoft.com/v1.0/users'
              + `?$filter=${encodeURIComponent(filtro)}`
              + '&$select=id,displayName,mail,userPrincipalName,jobTitle,department&$top=100',
            { headers }
          ).pipe(
            map((res) => res?.value ?? []),
            catchError(() => of([] as PersonaDelTenant[]))
          );
        });

        return forkJoin(pedidos).pipe(map((listas) => listas.flat()));
      }),
      catchError(() => of([] as PersonaDelTenant[]))
    );
  }

  /**
   * Busca personas en el directorio del tenant.
   *
   * **Con SSO, las personas del sistema son las del tenant.** Traerlas de la
   * sincronización de ARMUS era la fuente equivocada: los empleados de Odoo
   * son otra población —en DEV, tres— y quien inicia sesión con Microsoft
   * puede no estar entre ellos.
   *
   * Va con un token propio para Graph: el de la sesión sirve para nuestra API,
   * no para leer el directorio. Si el permiso no está consentido en el tenant,
   * MSAL falla y **se devuelve el motivo**, que es accionable —hay que
   * otorgarlo— en vez de una lista vacía que parece que no hay nadie.
   */
  buscarEnElTenant(texto: string): Observable<{ personas: PersonaDelTenant[]; motivo?: string }> {
    const account = this.getAccount();

    if (!account) return of({ personas: [], motivo: 'No hay sesión de Microsoft.' });

    const filtro = texto.replace(/'/g, "''");

    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: ['User.ReadBasic.All']
      })
    ).pipe(
      switchMap((r) => this.http.get<{ value: PersonaDelTenant[] }>(
        'https://graph.microsoft.com/v1.0/users'
          + `?$search="displayName:${filtro}" OR "mail:${filtro}"`
          + '&$select=id,displayName,mail,userPrincipalName,jobTitle,department&$top=15',
        { headers: { Authorization: `Bearer ${r.accessToken}`, ConsistencyLevel: 'eventual' } }
      )),
      map((r) => ({ personas: r?.value ?? [] })),
      catchError((e) => {
        /* Un 403 de Graph es «el token no trae el permiso»; un fallo de MSAL
           es «no se pudo emitir un token que lo traiga». Se dicen distinto
           porque mandan a lugares distintos: uno al tenant, el otro a volver a
           iniciar sesión. */
        const deMsal = e?.errorCode === 'consent_required'
          || e?.errorCode === 'interaction_required'
          || e?.errorCode === 'login_required';

        return of({
          personas: [],
          motivo: deMsal
            ? 'Hay que volver a iniciar sesión para poder leer el directorio: la sesión '
              + 'actual se abrió sin ese permiso.'
            : e?.status === 403
              ? 'Falta que un administrador del tenant otorgue el permiso User.ReadBasic.All.'
              : 'No se pudo leer el directorio de Microsoft 365.'
        });
      })
    );
  }

  /** Olvida el token guardado: la próxima llamada pide uno nuevo */
  olvidarToken(): void {
    this.idTokenFromLogin = null;
    this.venceEn = 0;
  }

  private getApiBaseUrl(): string {
    const env = environment as { useProxy?: boolean; accountingApi?: { baseUrl: string; proxyBaseUrl?: string } };
    return env?.useProxy && env?.accountingApi?.proxyBaseUrl !== undefined
      ? (env.accountingApi.proxyBaseUrl ?? '')
      : environment.accountingApi.baseUrl;
  }

  private buildApiUrl(path: string): string {
    const base = this.getApiBaseUrl();
    const sep = base.endsWith('/') || path.startsWith('/') ? '' : '/';
    return `${base}${sep}${path}`;
  }

  /**
   * El token de sesion, para que otros servicios puedan llamar a la API sin
   * pasar por aca. Era privado, y eso obligaba a que cada endpoint nuevo se
   * agregara a este archivo, que ya tiene un metodo por cada catalogo.
   */
  getToken(): Observable<string | null> {
    // En modo vista previa (solo localhost) no hay sesion de Azure: se usa un
    // token ficticio para que la peticion se haga y la conteste el
    // previewInterceptor con los datos sembrados.
    if (enModoPreview()) return of('preview');

    const hardcoded = (environment as { hardcodedToken?: string }).hardcodedToken?.trim();
    if (hardcoded) return of(hardcoded);
    return this.getTokenFromMsal();
  }

  /**
   * El token para un pedido que alguien hizo: si no hay, se manda al login.
   *
   * **Lo usan todos los servicios de pantalla**, no `getToken()`. Sin token,
   * cada `conToken` devolvía `null` sin llamar al backend y la pestaña quedaba
   * vacía como si no hubiera datos; el 401 del interceptor nunca saltaba
   * porque el pedido no salía. Sigue devolviendo `null` para que ninguna
   * pantalla reviente mientras se navega al login.
   *
   * Las consultas de fondo (el semáforo de sistemas) siguen con `getToken()`:
   * una consulta que nadie pidió no puede echar a nadie.
   */
  tokenParaPedir(): Observable<string | null> {
    return this.getToken().pipe(
      tap((token) => {
        if (!token) this.sesionVencida();
      })
    );
  }

  private fetchWithToken<T>(url: string, freshPayload?: AuthenticationResult): Observable<T | null> {
    const token$ = freshPayload?.idToken
      ? of(freshPayload.idToken).pipe(tap((t) => { if (t) this.idTokenFromLogin = t; }))
      : this.getToken();

    return token$.pipe(
      switchMap((idToken) => {
        /* **Sin token, la sesión se terminó: se manda a iniciar sesión.**

           Antes devolvía `null` sin salir a la red, y el tablero lo tomaba por
           una lista vacía: mostraba «Todavía no hay proyectos» a alguien que
           tenía proyectos asignados (le pasó a Facu el 14/9/2026 en deve, con
           el token de Microsoft vencido y sin poder renovarse). El aviso de 401
           del interceptor no saltaba porque el pedido nunca llegaba al backend. */
        if (!idToken) {
          this.sesionVencida();

          return throwError(() => new Error('Sesión vencida'));
        }

        return this.http.get<T>(url, { headers: { Authorization: `Bearer ${idToken}` } });
      })
    );
  }

  /**
   * Lleva al login avisando que la sesión venció, igual que el interceptor
   * de 401: primero se borra la cuenta, porque si queda el login cree que hay
   * sesión y devuelve al tablero en un ida y vuelta sin fin.
   */
  private sesionVencida(): void {
    if (enModoPreview() || this.router.url.startsWith('/login')) return;

    this.cerrarSesionLocal();
    this.router.navigate(['/login'], { queryParams: { vencida: 1 } });
  }

  fetchProjects(freshPayload?: AuthenticationResult): Observable<ProjectsApiResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projects);
    return this.fetchWithToken<ProjectsApiResponse>(url, freshPayload);
  }

  fetchProjectByCode(
    code: string,
    opciones?: { moneda?: string | null; periodo?: string | null }
  ): Observable<ProjectByCodeResponse | null> {
    /* La moneda y el periodo viajan en la url: el backend devuelve los
       importes ya convertidos y dice en cual quedaron. Si los convirtiera la
       pantalla, cada pestania tendria que repetir la regla. */
    const filtros = new URLSearchParams();
    if (opciones?.moneda) filtros.set('moneda', opciones.moneda);
    if (opciones?.periodo) filtros.set('periodo', opciones.periodo);

    const query = filtros.toString();
    const url = this.buildApiUrl(
      `${environment.accountingApi.endpoints.projectByCode}/${encodeURIComponent(code)}` +
      (query ? `?${query}` : '')
    );
    return this.fetchWithToken<ProjectByCodeResponse>(url);
  }

  fetchProjectsAndDetails(): Observable<ProjectDetailResponse[]> {
    return this.fetchProjects().pipe(
      tap((res) => console.log('[AuthService] API Projects:', res)),
      switchMap((res) => {
        const body = res as ProjectsApiResponse | null;
        const codes = (body?.data ?? []).map((item) => item.code).filter((c): c is string => !!c);
        if (codes.length === 0) return of([]);
        return forkJoin(codes.map((code) => this.fetchProjectByCode(code)));
      }),
      map((details) =>
        (details ?? [])
          .map((item) => {
            const wrapped = item as { data?: ProjectDetailResponse };
            return wrapped?.data ?? (item as ProjectDetailResponse);
          })
          .filter((p): p is ProjectDetailResponse => p?.code != null)
      )
    );
  }

  fetchProjectStatus(): Observable<ProjectStatusResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projectStatus);
    return this.fetchWithToken<ProjectStatusResponse>(url);
  }

  fetchProjectTypes(): Observable<ProjectTypesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projectTypes);
    return this.fetchWithToken<ProjectTypesResponse>(url);
  }

  fetchRegions(): Observable<RegionsResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.regions);
    return this.fetchWithToken<RegionsResponse>(url);
  }

  fetchCompanies(): Observable<CompaniesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.companies);
    return this.fetchWithToken<CompaniesResponse>(url);
  }

  fetchBusinessUnits(): Observable<BusinessUnitsResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.businessUnits);
    return this.fetchWithToken<BusinessUnitsResponse>(url);
  }

  fetchCountries(): Observable<CountriesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.countries);
    return this.fetchWithToken<CountriesResponse>(url);
  }

  fetchProductTypes(): Observable<ProductTypesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.productTypes);
    return this.fetchWithToken<ProductTypesResponse>(url);
  }

  fetchResponsiblesRoles(): Observable<ResponsiblesRolesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.responsiblesRoles);
    return this.fetchWithToken<ResponsiblesRolesResponse>(url);
  }

  fetchServiceTypes(productType: number): Observable<unknown> {
    const path = `${environment.accountingApi.endpoints.servicesTypes}/${encodeURIComponent(String(productType))}`;
    const url = this.buildApiUrl(path);
    return this.fetchWithToken<unknown>(url);
  }

  fetchSystemDataForProjectDetail(): Observable<{
    projectStatus: ProjectStatusResponse | null;
    projectTypes: ProjectTypesResponse | null;
    regions: RegionsResponse | null;
    companies: CompaniesResponse | null;
    businessUnits: BusinessUnitsResponse | null;
    countries: CountriesResponse | null;
    productTypes: ProductTypesResponse | null;
    responsiblesRoles: ResponsiblesRolesResponse | null;
  }> {
    /* Cada catalogo atrapa su propio error y devuelve null.

       Sin esto, forkJoin corta con el primero que falla y se lleva puesto todo
       el detalle: un catalogo caido dejaba la pantalla entera en blanco, con
       los indicadores en '-' y sin nombre de proyecto. Un desplegable sin
       opciones es un problema chico; la pantalla vacia es uno grande. */
    const tolerante = <T>(fuente: Observable<T | null>) =>
      fuente.pipe(catchError(() => of(null)));

    return forkJoin({
      projectStatus: tolerante(this.fetchProjectStatus()),
      projectTypes: tolerante(this.fetchProjectTypes()),
      regions: tolerante(this.fetchRegions()),
      companies: tolerante(this.fetchCompanies()),
      businessUnits: tolerante(this.fetchBusinessUnits()),
      countries: tolerante(this.fetchCountries()),
      productTypes: tolerante(this.fetchProductTypes()),
      responsiblesRoles: tolerante(this.fetchResponsiblesRoles())
    });
  }

  private fetchProjectsAfterLogin(event: EventMessage): void {
    const payload = event.payload as AuthenticationResult;
    this.fetchProjects(payload).subscribe({
      next: (response) => console.log('[API Projects] Respuesta tras login:', response),
      error: (err) => console.error('[API Projects] Error:', err)
    });
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Get the current user account
   */
  getAccount() {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  login(redirectUrl?: string): void {
    if (redirectUrl) {
      sessionStorage.setItem('postLoginRedirectUrl', redirectUrl);
    }
    Promise.resolve(this.initializeMsal()).then(() => {
      this.msalService.loginRedirect();
    });
  }

  /**
   * Entrar de cero, tirando todo lo que había guardado.
   *
   * **`login()` no alcanza cuando lo que está mal es lo guardado.** Con una
   * cuenta en el caché de MSAL y una sesión abierta en Microsoft, el viaje de
   * ida y vuelta es instantáneo y silencioso: se vuelve con exactamente el
   * mismo estado roto, y la pantalla dice otra vez que no se pudo entrar sin
   * que nada haya cambiado. Quedarse apretando el botón no arregla nada.
   *
   * Acá se borra el token, se limpia el caché de MSAL, se suelta el tope de
   * reintentos y se pide `select_account`, que obliga a Microsoft a preguntar
   * de verdad en vez de resolverlo solo.
   */
  entrarDeCero(redirectUrl?: string): void {
    if (redirectUrl) {
      sessionStorage.setItem('postLoginRedirectUrl', redirectUrl);
    }

    this.olvidarToken();

    try {
      sessionStorage.removeItem('loginReintentoSSO');
    } catch { /* una pestaña sin storage no es motivo para no intentar */ }

    Promise.resolve(this.initializeMsal())
      .then(() => this.msalService.instance.clearCache?.())
      .catch(() => undefined)
      .then(() => {
        this.msalService.loginRedirect({
          scopes: environment.apiConfig.scopes,
          /* La clave de todo esto: sin `prompt`, Microsoft reusa la sesión que
             ya tiene y devuelve lo mismo que ya no servía. */
          prompt: 'select_account'
        });
      });
  }
  logout(): void {
    this.idTokenFromLogin = null;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
      onRedirectNavigate: () => {
        // Return false = no ir a Microsoft logout → solo limpiamos caché local
        // Redirigimos manualmente a /login
        this.router.navigateByUrl('/login');
        return false;
      }
    });
  }

  /**
   * Logout con redirect a Microsoft (cierra todas las cuentas) - usar solo si se necesita.
   */
  logoutFull(): void {
    this.idTokenFromLogin = null;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`
    });
  }

  /**
   * Get interaction status observable
   */
  getInteractionStatus(): Observable<InteractionStatus> {
    return this.msalBroadcastService.inProgress$;
  }

  /**
   * Check if interaction is in progress
   */
  isInteractionInProgress(): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      map(status => status !== InteractionStatus.None)
    );
  }
}
